import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';

import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid';

import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles, Theme } from '@material-ui/core/styles';

import {
  COLOR_PRIMARY,
  COLOR_TOOLTIP_BACKGROUND,
  COLOR_GREEN,
  COLOR_RED,
  COLOR_E,
  Top10ManagerDummyData,
  exchangeRates,
} from 'src/const';

import { ListingsProp } from 'src/interfaces/iproptypes';
import { Host, Listing } from 'src/interfaces/idashboard';
import {
  getVRMs,
  convertCurrency,
  formatCurrency,
  convertListingsDictToArray,
} from 'src/utils/functions';

import { InfoTooltip } from '../Tooltips';
import { TooltipCard } from '.';
import UnlockButton from '../CTA/UnlockButton';
import postOnSlack from 'src/core/postOnSlack';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_2, COLOR_TEXT_1, SIDEBAR_COLOR } from 'src/colors';
import { FormControlLabel, Radio, RadioGroup, TextField, Tooltip } from '@mui/material';

const cellCol = 'rgba(201, 207, 218, 0.15)';

const loadingStyle = makeStyles((theme: Theme) => ({
  colorPrimary: {
    backgroundColor: (props: any) => props.backgroundColor,
  },
  barColorPrimary: {
    backgroundColor: COLOR_TOOLTIP_BACKGROUND,
  },
}));

interface Top10ManagerCardProps {
  isDisabled: boolean;
  listings: any;
  currentMarket: string;
  currency: string;
  multipleMarkersCallLoading: Boolean;
  handleOnSampleClick: () => void;
  user: any;
  matches: boolean;
}

const Top10ManagerCard = ({
  isDisabled,
  listings,
  currentMarket,
  currency,
  multipleMarkersCallLoading,
  handleOnSampleClick,
  user,
  matches,
}: Top10ManagerCardProps) => {
  const { colorPrimary, barColorPrimary } = loadingStyle({ backgroundColor: COLOR_PRIMARY });
  const [showVRMLeadForm, setShowVRMLeadForm] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  let data = Top10ManagerDummyData;
  let allData = Top10ManagerDummyData;

  let processedData: Host[] = [];
  let allProcessedData: Host[] = [];

  if (
    // !isDisabled &&
     !listings.loading && Object.keys(listings.data).length > 0) {
    const allDataObject = getVRMs(convertListingsDictToArray(listings.data) as Listing[]);
    processedData = allDataObject.top10ManagerArray;
    allProcessedData = allDataObject.allManagers;
    // console.log('Processed Data: ', allProcessedData);

    data = processedData.map(data => {
      return {
        id: data.hostId,
        name: data.name,
        revenue: data.revenue,
        listings: data.totalNumberOfListings,
        image: data.listingImage,
        totalRating: data.totalRating,
      };
    });

    allData = allProcessedData.map(data => {
      return {
        id: data.hostId,
        name: data.name,
        revenue: data.revenue,
        listings: data.totalNumberOfListings,
        image: data.listingImage,
        totalRating: data.totalRating,
      };
    });
  }

  const listViewCols: GridColDef[] = [
    { field: 'listingID', headerName: 'Listing ID', width: 200, hide: true },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            position: 'relative',
            fontWeight: 400,
            fontSize: '16px',
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            border: 'none',
          }}
        >
          <a
            href={`https://www.airbnb.com/users/show/${params.row.id}`}
            rel="nofollow"
            target="_blank"
          >
            <img
              src={params.row.image}
              alt={''}
              style={{
                height: '40px',
                width: '40px',
                objectFit: 'cover',
                borderRadius: '4px',
              }}
            />
          </a>
          <abbr
            title={params.row.name}
            tabIndex={0}
            style={{ textDecoration: 'none', cursor: 'help' }}
          >
            {params.row.name}
          </abbr>
        </Box>
      ),
    },
    {
      field: 'totalRevenue',
      headerName: 'Total Revenue (Last 12 Months)',
      flex: 1,
      minWidth: 100,
      valueGetter: params => {
        return Number(params.row.revenue);
      },
      renderCell: (params: GridRenderCellParams) => (
        <>
          {exchangeRates[currency].symbol}{' '}
          {formatCurrency(convertCurrency(currency, params.row.revenue) + '')}
        </>
      ),
    },
    {
      field: 'totalListings',
      headerName: 'Total number of listings in this city',
      flex: 1,
      minWidth: 100,
      valueGetter: params => {
        return params.row.listings;
      },
      renderCell: (params: GridRenderCellParams) => <Typography>{params.row.listings}</Typography>,
    },
    {
      field: 'avgRating',
      headerName: 'Average User Rating',
      flex: 1,
      minWidth: 70,
      valueGetter: params => {
        return Number(params.row.totalRating / params.row.listings);
      },
      renderCell: (params: GridRenderCellParams) => (
        <Typography>{(params.row.totalRating / params.row.listings).toFixed(1)}</Typography>
      ),
    },
  ];

  const hostDummyDataColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 200, hide: true },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      editable: false,
    },
    {
      field: 'profile',
      headerName: 'Host Profile',
      width: 200,
      editable: false,
      renderCell: (params) => {
        return (
          <Tooltip title={params.value} arrow>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <a href={params.value} target="_blank">{params.value}</a>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'listingCount',
      headerName: 'Number of Listings',
      width: 100,
      editable: false,
    },
    {
      field: 'website',
      headerName: 'Website',
      width: 200,
      editable: false,
      renderCell: (params) => {
        return (
          <Tooltip title={params.value} arrow>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <a href={params.value} target="_blank">{params.value}</a>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const hostDummyDataRows = [
    {
      id: 1,
      name: 'MadeComfy',
      profile: 'https://www.airbnb.com/users/show/279001183',
      listingCount: '964',
      website: 'https://madecomfy.com.au/about-us/contact/',
    },
    {
      id: 2,
      name: 'Apac',
      profile: 'https://www.airbnb.com/users/show/138649185',
      listingCount: '546',
      website: 'https://www.silverdoorapartments.com/contact/',
    },
    {
      id: 3,
      name: 'Tushita',
      profile: 'https://www.airbnb.com/users/show/156409670',
      listingCount: '311',
      website: 'https://www.silverdoorapartments.com',
    },
    {
      id: 4,
      name: 'MadeComfy Sydney',
      profile: 'https://www.airbnb.com/users/show/235137306',
      listingCount: '270',
      website: '',
    },
    {
      id: 5,
      name: 'Advante Homes',
      profile: 'https://www.airbnb.com/users/show/343442154',
      listingCount: '209',
      website: '',
    },
    {
      id: 6,
      name: 'AirKeeper',
      profile: 'https://www.airbnb.com/users/show/526140891',
      listingCount: '195',
      website: 'https://airkeeper.com.au/',
    },
    {
      id: 7,
      name: "L'Abode Accommodation Specialist",
      profile: 'https://www.airbnb.com/users/show/7409213',
      listingCount: '193',
      website: 'https://labodeaccommodation.com.au/contact/',
    },
    {
      id: 8,
      name: 'The Apartment Service',
      profile: 'https://www.airbnb.com/users/show/15739069',
      listingCount: '133',
      website: 'https://www.apartmentservice.com.au/contact-us',
    },
    {
      id: 9,
      name: 'Ken',
      profile: 'https://www.airbnb.com/users/show/288743418',
      listingCount: '126',
      website: '',
    },
    {
      id: 10,
      name: 'Tim',
      profile: 'https://www.airbnb.com/users/show/301753450',
      listingCount: '121',
      website: '',
    },
    {
      id: 11,
      name: 'Hotelesque',
      profile: 'https://www.airbnb.com/users/show/2450066',
      listingCount: '116',
      website: 'https://hotelesque.com.au/about-us/',
    },
    {
      id: 12,
      name: 'Hometime',
      profile: 'https://www.airbnb.com/users/show/216159830',
      listingCount: '115',
      website: '',
    },
    {
      id: 13,
      name: 'PropertyProviders',
      profile: 'https://www.airbnb.com/users/show/16357713',
      listingCount: '106',
      website: 'https://www.propertyproviders.com.au/contact-us/',
    },
    {
      id: 14,
      name: 'AirCabin',
      profile: 'https://www.airbnb.com/users/show/484473459',
      listingCount: '100',
      website: '',
    },
  ];

  const VRMLeadForm = () => {
    const [email, setEmail] = useState<string>(user.email);
    const [shareDataPulically, setShareDataPulically] = useState<string>('');
    const [companyName, setCompanyName] = useState<string>('');
    const [market, setMarket] = useState<string>(currentMarket);
    const [additionNote, setAdditionalNote] = useState<string>('');
    const [showSampleDataModal, setShowSampleDataModal] = useState<boolean>(false);
    const [minNoOfAirbnbListings, setMinNoOfAirbnbListings] = useState<string>('');
    const [maxNoOfAirbnbListings, setMaxNoOfAirbnbListings] = useState<string>('');
    const [
      needCompanyWebsiteForProfessionalHost,
      setNeedCompanyWebsiteForProfessionalHost,
    ] = useState<string>('');

    const closeVRMLeadFormModal = () => {
      setShowVRMLeadForm(false);
    };

    const handleOnSubmitExportDataClick = async () => {
      let message = `VRM Lead Form Submit\nUser: ${email}\nWant to share data publicly: ${shareDataPulically}\nMinimum number of Airbnb listings: ${minNoOfAirbnbListings}\nMaximum number of Airbnb listings: ${maxNoOfAirbnbListings}\nNeed the company websites of professional Airbnb hosts: ${needCompanyWebsiteForProfessionalHost}\nCompany name/Individual use: ${companyName}\n Market: ${market}\n Additional notes: ${additionNote}`;
      let sendSlackMessage = await postOnSlack('C072095QUU9', message);
      closeVRMLeadFormModal();
    };

    const SampleVrmDataModal = () => {
      const closeSampleVRMFormModal = () => {
        setShowSampleDataModal(false);
      };
      return (
        <Modal
          open={showSampleDataModal}
          onClose={closeSampleVRMFormModal}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Grid
            container
            item
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              position: 'relative',
              width: matches ? '80%' : '60%',
            }}
          >
            <Box sx={{ position: 'absolute', top: '10px', right: '10px' }}>
              <CloseIcon
                sx={{ cursor: 'pointer', color: COLOR_TEXT_1 }}
                onClick={closeSampleVRMFormModal}
              />
            </Box>
            <Grid
              item
              xs={12}
              sx={{
                padding: '30px',
                border: 'none',
                backgroundColor: SIDEBAR_COLOR,
                borderRadius: '10px',
              }}
            >
              <Box sx={{ height: '500px', width: '100%' }}>
                <DataGrid
                  sortingOrder={['desc']}
                  sx={{ backgroundColor: 'white' }}
                  rows={hostDummyDataRows}
                  columns={hostDummyDataColumns}
                  disableColumnFilter={true}
                  disableColumnMenu={true}
                />
              </Box>
            </Grid>
          </Grid>
        </Modal>
      );
    };

    return (
      <Modal
        open={showVRMLeadForm}
        onClose={closeVRMLeadFormModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Grid
          container
          item
          // xs={12}
          sx={{
            backgroundColor: 'white',
            // borderRadius: '8px',
            maxHeight: matches ? '500px' : '700px',
            position: 'relative',
            width: '40%',
            minWidth: '300px',
          }}
        >
          <SampleVrmDataModal />
          <Box sx={{ position: 'absolute', top: '10px', right: '10px' }}>
            <CloseIcon
              sx={{ cursor: 'pointer', color: SIDEBAR_COLOR }}
              onClick={closeVRMLeadFormModal}
            />
          </Box>
          <Grid
            item
            xs={12}
            sx={{
              height: '50px',
              padding: '10px',
              backgroundColor: COLOR_PRIMARY_1,
              border: 'none',
              color: SIDEBAR_COLOR,
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px #0002',
            }}
          >
            <Typography variant={matches ? 'h6' : 'h5'} sx={{ textAlign: 'center' }}>
              Download Airbnb Host Data
            </Typography>
          </Grid>
          <Grid
            container
            item
            xs={12}
            sx={{ maxHeight: matches ? '330px' : '530px', overflow: 'auto' }}
          >
            <Grid
              container
              item
              xs={12}
              sx={{
                padding: '20px 40px',
              }}
            >
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Email:
                </Typography>
                <TextField
                  id="outlined-basic"
                  label=""
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      padding: '10px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Do you need to share this data publicly?
                </Typography>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={shareDataPulically}
                  onChange={e => {
                    setShareDataPulically(e.target.value);
                  }}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Minimum number of Airbnb listings
                </Typography>
                <TextField
                  id="outlined-basic"
                  label=""
                  variant="outlined"
                  fullWidth
                  value={minNoOfAirbnbListings}
                  onChange={e => {
                    setMinNoOfAirbnbListings(e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      padding: '10px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Maximum number of Airbnb listings
                </Typography>
                <TextField
                  id="outlined-basic"
                  label=""
                  variant="outlined"
                  fullWidth
                  value={maxNoOfAirbnbListings}
                  onChange={e => {
                    setMaxNoOfAirbnbListings(e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      padding: '10px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Do you need the company websites of professional Airbnb hosts? (Will incur extra
                  $1 per website)
                </Typography>
                <TextField
                  id="outlined-basic"
                  label=""
                  variant="outlined"
                  fullWidth
                  value={needCompanyWebsiteForProfessionalHost}
                  onChange={e => {
                    setNeedCompanyWebsiteForProfessionalHost(e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      padding: '10px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  What is your company name? or is this for individual use?
                </Typography>
                <TextField
                  id="outlined-basic"
                  label=""
                  variant="outlined"
                  fullWidth
                  value={companyName}
                  onChange={e => {
                    setCompanyName(e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      padding: '10px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Markets (Write countries if you need nationwide data)
                </Typography>
                <TextField
                  id="outlined-basic"
                  label=""
                  variant="outlined"
                  fullWidth
                  value={market}
                  onChange={e => {
                    setMarket(e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      padding: '10px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Any Other Notes?
                </Typography>
                <TextField
                  id="outlined-multiline-static"
                  label=""
                  variant="outlined"
                  fullWidth
                  multiline
                  value={additionNote}
                  onChange={e => {
                    setAdditionalNote(e.target.value);
                  }}
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <Button
                  sx={{
                    width: '100%',
                    backgroundColor: COLOR_PRIMARY_2,
                    color: SIDEBAR_COLOR,
                    '&:hover': { backgroundColor: COLOR_PRIMARY_2, color: SIDEBAR_COLOR },
                    borderRadius: '10px',
                  }}
                  onClick={() => {
                    setShowSampleDataModal(true);
                  }}
                >
                  See Demo Data
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ height:'60px', padding:'10px' }}>
            <Button
              sx={{
                width: '100%',
                backgroundColor: COLOR_PRIMARY_1,
                color: SIDEBAR_COLOR,
                borderRadius: '10px',
                '&:hover': { backgroundColor: COLOR_PRIMARY_1, color: SIDEBAR_COLOR },
              }}
              onClick={handleOnSubmitExportDataClick}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Modal>
    );
  };

  return (
    <>
      <Card>
        <CardContent>
          {multipleMarkersCallLoading && !isDisabled && (
            <Grid item sx={{ width: '100%', marginBottom: '15px', color: COLOR_PRIMARY }}>
              <LinearProgress
                classes={{ colorPrimary: colorPrimary, barColorPrimary: barColorPrimary }}
              />
            </Grid>
          )}

          <Container>
            <Grid container item xs={12} sx={{ marginTop: '20px' }}>
              <VRMLeadForm />
              <Typography
                variant="body2"
                component="h2"
                sx={{
                  fontWeight: 400,
                  fontSize: '17px',
                }}
              >
                Top Vacation Rental Managers in this market
                {/* {isDisabled ? ' Demo Market: City of Brighton and Hove' : ' this market '} */}
              </Typography>
            </Grid>

            <TableContainer
              sx={{
                position: 'relative',
                width: {
                  xs: '90vw',
                  sm: '100%',
                },
              }}
            >
              <Table
                aria-label="Top 10 rental managers in the market"
                sx={{
                  marginTop: '25px',
                  minWidth: 650,
                  overflow: 'scroll',
                  // filter: isDisabled ? 'blur(4px)' : 'none',
                }}
              >
                <TableHead>
                  <TableRow sx={{ borderTop: '2px solid #e8e8e866' }}>
                    <TableCell
                      sx={{
                        borderLeft: '2px solid #e8e8e866',
                        borderRight: '2px solid #e8e8e866',
                        fontWeight: 500,
                        fontSize: '16px',
                        minWidth: '120px',
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 500, fontSize: '16px', borderRight: '2px solid #e8e8e866' }}
                    >
                      Total Revenue (Last 12 Months)
                    </TableCell>
                    <InfoTooltip
                      title={
                        <TooltipCard content={'Total Number of Listings in ' + currentMarket} />
                      }
                    >
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: '16px',
                          borderRight: '2px solid #e8e8e866',
                        }}
                      >
                        Total Number of Listings in{' '}
                        {currentMarket.length > 15
                          ? currentMarket.substr(0, 12) + '...'
                          : currentMarket}
                      </TableCell>
                    </InfoTooltip>
                    <TableCell
                      sx={{ fontWeight: 500, fontSize: '16px', borderRight: '2px solid #e8e8e866' }}
                    >
                      Average User Rating
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((data, idx) => (
                    <TableRow
                      key={'TableTop10' + idx}
                      sx={{
                        '&:nth-child(2n-1)': {
                          backgroundColor: cellCol,
                        },
                        '&': {
                          borderBottom: '2px solid #e8e8e866 !important',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: 'relative',
                          fontWeight: 400,
                          fontSize: '16px',
                          display: 'flex',
                          gap: '16px',
                          alignItems: 'center',
                          border: 'none',
                        }}
                      >
                        <a
                          href={`https://www.airbnb.com/users/show/${data.id}`}
                          rel="nofollow"
                          target="_blank"
                        >
                          <img
                            src={data.image}
                            alt={''}
                            style={{
                              height: '40px',
                              width: '40px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                            }}
                          />
                        </a>
                        <abbr
                          title={data.name}
                          tabIndex={0}
                          style={{ textDecoration: 'none', cursor: 'help' }}
                        >
                          {data.name}
                        </abbr>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 400, fontSize: '16px' }}>
                        {exchangeRates[currency].symbol}{' '}
                        {formatCurrency(convertCurrency(currency, data.revenue) + '')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 400, fontSize: '16px' }}>
                        {data.listings}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 400, fontSize: '16px' }}>
                        {(data.totalRating / data.listings).toFixed(1)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* {isDisabled && <UnlockButton handleOnSampleClick={handleOnSampleClick} />} */}
            </TableContainer>

            {/* {!isDisabled && ( */}
              <Button
                sx={{ float: 'right', marginTop: '20px', marginBottom: '20px' }}
                onClick={() => setShowVRMLeadForm(true)}
              >
                See All
              </Button>
             {/* )} */}
          </Container>
        </CardContent>
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
      >
        <Container>
          <Box
            sx={{
              background: '#fff',
              height: '80vh',
              width: '90vw',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <DataGrid
              rows={allData}
              columns={listViewCols}
              checkboxSelection={false}
              components={{
                Toolbar: () => (
                  <GridToolbarContainer>
                    <GridToolbarExport />
                  </GridToolbarContainer>
                ),
              }}
              disableSelectionOnClick={true}
            />
          </Box>
        </Container>
      </Modal>
    </>
  );
};

// export default Top10ManagerCard;

export default React.memo(Top10ManagerCard);
